import { t } from 'i18next';

import { EMPTY_VALUE, StudioFeature } from './studioFeatureGating';
import {
  SellableStudioPlanId,
  STUDIO_EXTRA_APP_MONTHLY_DOLLARS,
  STUDIO_INCLUDED_APPS,
  STUDIO_MAX_MONTHLY_CREDITS,
  StudioBaseTiers,
} from './studioPlanFeatures';

/** Order the sellable plans appear across the comparison columns. */
export const SELLABLE_PLAN_IDS: SellableStudioPlanId[] = [
  'starter',
  'professional',
  'advanced',
];

/**
 * A comparison row is either a per-plan text value (active contacts, credits, …)
 * or a boolean entitlement whose check/dash is derived from the gating matrix in
 * {@link studioFeatureGating} — so the table can never disagree with the gates.
 */
export type StudioComparisonCell =
  | { kind: 'value'; values: Record<SellableStudioPlanId, string> }
  | { kind: 'feature'; feature: StudioFeature };

export interface StudioComparisonRow {
  label: string;
  cell: StudioComparisonCell;
}

export interface StudioComparisonGroup {
  title: string;
  rows: StudioComparisonRow[];
}

// The additional-internal-user price is the same figure shown as the per-user
// badge on the plan cards, so both read from one source; Starter has none.
const additionalUserValues = (): Record<SellableStudioPlanId, string> => ({
  starter: EMPTY_VALUE,
  professional: t(($) => $.page_studio_plans.feat.per_user_badge.pro),
  advanced: t(($) => $.page_studio_plans.feat.per_user_badge.advanced),
});

const sameForAllPlans = (
  value: string,
): Record<SellableStudioPlanId, string> => ({
  starter: value,
  professional: value,
  advanced: value,
});

// Ceiling of the extra-credits ladder a plan can be topped up to. The plans API
// carries each plan's included grant but not this cap, so it comes from the same
// ladder the card dropdown is built from.
const MAX_MONTHLY_CREDITS = STUDIO_MAX_MONTHLY_CREDITS;

// Monthly build credits come from the plans API — the workspace's own included
// grant, which a negotiated override can move away from the catalog default.
const buildCreditValues = (
  baseTiers: StudioBaseTiers,
): Record<SellableStudioPlanId, string> => {
  const creditValue = (planId: SellableStudioPlanId): string => {
    const tier = baseTiers.get(planId);
    if (!tier) return EMPTY_VALUE;
    return t(($) => $.page_studio_plans.comparison.values.build_credits, {
      credits: tier.credits.toLocaleString('en-US'),
      max: MAX_MONTHLY_CREDITS.toLocaleString('en-US'),
    });
  };
  return {
    starter: creditValue('starter'),
    professional: creditValue('professional'),
    advanced: creditValue('advanced'),
  };
};

/**
 * The plan comparison matrix rendered by the upgrade modal. Value rows carry
 * copy per plan; feature rows carry a {@link StudioFeature} the table resolves to
 * a check/dash via `planHasFeatureAccess`. Groups and rows mirror the design.
 */
export function getStudioPlanComparison(
  baseTiers: StudioBaseTiers,
): StudioComparisonGroup[] {
  return [
    {
      title: t(($) => $.page_studio_plans.comparison.groups.users),
      rows: [
        {
          label: t(
            ($) => $.page_studio_plans.comparison.labels.active_contacts,
          ),
          cell: {
            kind: 'value',
            values: {
              starter: t(
                ($) =>
                  $.page_studio_plans.comparison.values.active_contacts.starter,
              ),
              professional: t(
                ($) =>
                  $.page_studio_plans.comparison.values.active_contacts.pro,
              ),
              advanced: t(
                ($) =>
                  $.page_studio_plans.comparison.values.active_contacts
                    .advanced,
              ),
            },
          },
        },
        {
          label: t(($) => $.page_studio_plans.comparison.labels.internal_users),
          cell: {
            kind: 'value',
            values: {
              starter: t(
                ($) =>
                  $.page_studio_plans.comparison.values.internal_users.starter,
              ),
              professional: t(
                ($) => $.page_studio_plans.comparison.values.internal_users.pro,
              ),
              advanced: t(
                ($) =>
                  $.page_studio_plans.comparison.values.internal_users.advanced,
              ),
            },
          },
        },
        {
          label: t(
            ($) => $.page_studio_plans.comparison.labels.additional_user,
          ),
          cell: { kind: 'value', values: additionalUserValues() },
        },
      ],
    },
    {
      title: t(($) => $.page_studio_plans.comparison.groups.app_builder),
      rows: [
        {
          label: t(($) => $.page_studio_plans.comparison.labels.build_credits),
          cell: { kind: 'value', values: buildCreditValues(baseTiers) },
        },
        {
          label: t(($) => $.page_studio_plans.comparison.labels.apps_included),
          cell: {
            kind: 'value',
            values: sameForAllPlans(STUDIO_INCLUDED_APPS.toString()),
          },
        },
        {
          label: t(($) => $.page_studio_plans.comparison.labels.extra_apps),
          cell: {
            kind: 'value',
            values: sameForAllPlans(
              t(($) => $.page_studio_plans.comparison.values.extra_apps, {
                price: STUDIO_EXTRA_APP_MONTHLY_DOLLARS,
              }),
            ),
          },
        },
        {
          label: t(($) => $.page_studio_plans.feat.model_selection),
          cell: { kind: 'feature', feature: 'modelSelection' },
        },
      ],
    },
    {
      title: t(($) => $.page_studio_plans.comparison.groups.features),
      rows: [
        {
          label: t(($) => $.page_studio_plans.feat.custom_domain),
          cell: { kind: 'feature', feature: 'customDomain' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.api_integrations),
          cell: { kind: 'feature', feature: 'api' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.multi_company_clients),
          cell: { kind: 'feature', feature: 'multiCompanyClients' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.automations),
          cell: { kind: 'feature', feature: 'automations' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.remove_badge),
          cell: { kind: 'feature', feature: 'removeBranding' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.audit_log),
          cell: { kind: 'feature', feature: 'auditLog' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.client_access_permissions),
          cell: { kind: 'feature', feature: 'clientAccessPermissions' },
        },
        {
          label: t(($) => $.page_studio_plans.feat.enforce_mfa),
          cell: { kind: 'feature', feature: 'enforceMfa' },
        },
      ],
    },
  ];
}
